/* Diagnosztikai gyűjtő a hibajelentő widgethez (2026-08-02, admin F4).
 *
 * A Sentry User Feedback mintája: a jó widget NEM kérdez, hanem gyűjt. A
 * felhasználótól csak azt kérjük, amit tényleg csak ő tud (mit csinált, mit
 * várt), a technikai kontextust magunk vesszük fel.
 *
 * ⚠ ADATVÉDELEM: a sikertelen API-hívásokból CSAK metódus, útvonal és státusz
 * kerül be. Kérés- vagy válasz-TÖRZS soha — egy 400-as válasz body-ja
 * számla-adatot tartalmazhatna, és ez a napló az adminban kereszt-tenant
 * módban látszik. (A szerver oldalán is van erre védőháló, lásd
 * `api/feedback.py::_sanitize_requests`.)
 */

const MAX_CONSOLE = 20
const MAX_REQUESTS = 10

const consoleErrors = []
const failedRequests = []

function push(list, item, max) {
  list.push({ ...item, at: new Date().toISOString() })
  while (list.length > max) list.shift()
}

/** Konzolhibák és elkapatlan kivételek gyűjtése körkörös pufferbe. */
export function installDiagnostics() {
  const origError = console.error
  console.error = (...args) => {
    try {
      push(consoleErrors, {
        message: args.map((a) => (a instanceof Error ? a.message : String(a)))
          .join(' ').slice(0, 500),
        source: 'console.error',
      }, MAX_CONSOLE)
    } catch { /* a gyűjtés sose törje el a konzolt */ }
    origError.apply(console, args)
  }

  window.addEventListener('error', (e) => {
    push(consoleErrors, {
      message: (e.message || String(e.error || '')).slice(0, 500),
      source: `${e.filename || '?'}:${e.lineno || 0}`,
    }, MAX_CONSOLE)
  })

  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason
    push(consoleErrors, {
      message: (r instanceof Error ? r.message : String(r)).slice(0, 500),
      source: 'unhandledrejection',
    }, MAX_CONSOLE)
  })
}

/* ── 2026-09-16: az útvonal azonosító NÉLKÜL megy a PostHogba ────────────────
 *
 * A körkörös puffer (fent) a TELJES útvonalat tartja meg — az helyi, és a
 * hibajelentéshez kell, hogy Gábor lássa, MELYIK bizonylatnál történt. A
 * PostHogba exportált attribútum viszont nem vihet üzleti azonosítót:
 * `/documents/1234` → `/documents/:id`. Ugyanaz az elv, mint a backend
 * log-csatornáján: a sablon megy ki, a konkrét érték helyben marad.
 *
 * ⚠ Csak a NUMERIKUS és az UUID-szegmenseket cseréljük. A `/nav-upo/sync`-ben
 * egyik sincs, tehát a végpont-szerkezet olvasható marad.
 */
function normalizeRoute(path) {
  return String(path || '')
    .split('?')[0]
    .split('/')
    .map((seg) => {
      if (/^\d+$/.test(seg)) return ':id'
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ':uuid'
      if (/^[0-9a-f]{16,}$/i.test(seg)) return ':hex'
      return seg
    })
    .join('/')
    .slice(0, 120)
}

/** Az axios-interceptor hívja minden sikertelen kérésnél. */
export function recordFailedRequest(method, path, status) {
  const http_method = String(method || '').toUpperCase().slice(0, 10)
  push(failedRequests, {
    method: http_method,
    path: String(path || '').slice(0, 200),
    status: typeof status === 'number' ? status : null,
  }, MAX_REQUESTS)

  // A hiba magától is jusson ki — ne csak akkor, ha a user hibát jelent.
  // ⚠ KONSTANS üzenet + három allowlistelt attribútum. Se URL-paraméter, se
  // válasz-törzs, se fejléc. A `logger` hiánya (régi SDK, kikapcsolt logs)
  // nem törhet el egy API-hívást, ezért try/catch.
  try {
    window.posthog?.logger?.warn?.('api_request_failed', {
      http_method,
      route: normalizeRoute(path),
      http_status: typeof status === 'number' ? status : null,
    })
  } catch { /* a diagnosztika sose törje el a kérést */ }
}

/** A widget beküldéskor ezt csatolja. */
export function collectContext() {
  let sessionUrl = null
  try {
    // A PostHog session replay linkje — ha a replay fut. Segít megnézni,
    // MIT csinált a felhasználó a hiba előtt.
    sessionUrl = window.posthog?.get_session_replay_url?.({ withTimestamp: true }) || null
  } catch { /* replay kikapcsolva vagy régi SDK */ }

  return {
    page_url: window.location.pathname + window.location.search,
    app_version: process.env.REACT_APP_VERSION || '2.1.0',
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    console_errors: consoleErrors.slice(),
    failed_requests: failedRequests.slice(),
    posthog_session_url: sessionUrl,
  }
}

/** Csak a UI-nak: hány hibát gyűjtöttünk (a widget kiírja, mit fog csatolni). */
export function contextCounts() {
  return { consoleErrors: consoleErrors.length, failedRequests: failedRequests.length }
}
